
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, CreditCard, ChevronDown } from "lucide-react";
import { navigation } from "../navigation/NavigationConfig";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useSessionContext } from "@supabase/auth-helpers-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface TopNavigationProps {
  userRole: 'borrower' | 'lender' | 'admin' | 'vendor' | null;
}

const TopNavigation = ({ userRole }: TopNavigationProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { session } = useSessionContext();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);

  const currentNavigation = navigation[userRole || 'borrower'] || [];

  const handleLogout = async () => {
    try {
      if (!session) {
        console.log("No active session found");
        navigate('/');
        return;
      }

      const { error } = await supabase.auth.signOut({ 
        scope: 'local'
      });
      
      if (error) {
        console.error('Error during local logout:', error);
        if (!error.message.includes('session_not_found')) {
          toast({
            title: "Error logging out",
            description: "There was a problem logging out. Please try again.",
            variant: "destructive",
          });
          return;
        }
      }

      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (globalError) {
        console.log('Global logout attempted:', globalError);
      }

      toast({
        title: "Logged out successfully",
        description: "You have been logged out of your account",
      });
      
      navigate('/');
      
    } catch (error) {
      console.error('Unexpected error during logout:', error);
      toast({
        title: "Error logging out",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background border-b">
      <div className="container mx-auto px-4">
        <div className="h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-2xl font-bold text-primary flex items-center gap-2">
              <CreditCard className="h-6 w-6" />
              Obotan
            </Link>
          </div>

          {isMobile ? (
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <nav className="flex flex-col gap-4">
                  {currentNavigation.map((item) => (
                    item.children ? (
                      <div key={item.name} className="space-y-2">
                        <div className="font-medium text-sm flex items-center gap-2 p-2">
                          <item.icon className="h-5 w-5" />
                          {item.name}
                        </div>
                        <div className="pl-6 space-y-1">
                          {item.children.map((child) => (
                            <Link
                              key={child.name}
                              to={child.href || "#"}
                              className="flex items-center gap-2 p-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-md"
                              onClick={() => setIsOpen(false)}
                            >
                              <child.icon className="h-4 w-4" />
                              {child.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Link
                        key={item.name}
                        to={item.href || "#"}
                        className="flex items-center gap-2 p-2 hover:bg-accent rounded-md"
                        onClick={() => setIsOpen(false)}
                      >
                        <item.icon className="h-5 w-5" />
                        {item.name}
                      </Link>
                    )
                  ))}
                </nav>
              </SheetContent>
            </Sheet>
          ) : (
            <div className="hidden md:flex items-center gap-6">
              {currentNavigation.map((item) => (
                item.children ? (
                  <DropdownMenu key={item.name}>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
                        <item.icon className="h-4 w-4" />
                        {item.name}
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      {item.children.map((child) => (
                        <DropdownMenuItem key={child.name} asChild>
                          <Link 
                            to={child.href || "#"} 
                            className="flex items-center gap-2 w-full"
                          >
                            <child.icon className="h-4 w-4" />
                            {child.name}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href || "#"}
                    className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                  >
                    <item.icon className="h-4 w-4" />
                    {item.name}
                  </Link>
                )
              ))}
            </div>
          )}

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="ml-4"
              >
                <LogOut className="h-5 w-5 text-destructive" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you sure you want to logout?</AlertDialogTitle>
                <AlertDialogDescription>
                  You will be redirected to the homepage and will need to log in again to access your account.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleLogout}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Logout
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </nav>
  );
};

export default TopNavigation;
